<template>
    <div class="pp-draw-results__inner-wrapper" ref="draw-results-wrapper">
        <PpDrawNumbersAccordion
            v-if="results"
            :table-title="winnigTableTitle"
            id="pp-draw-results"
            aria-controls="pp-draw-results-content"
            ref="resultsButton"
        >
            <component
                class="pp-draw-results-button__collapsible-content"
                :is="tableComponent"
                :results="results"
                id="pp-draw-results-content"
                role="region"
                aria-labelledby="pp-draw-results"
            ></component>
        </PpDrawNumbersAccordion>

        <PpDrawNumbersAccordion
            v-if="results.extraWins"
            table-title="Extra výhra"
            aria-controls="pp-draw-results-extra-win-content"
            id="pp-draw-results-extra-win"
        >
            <div
                class="pp-draw-results-button__collapsible-content"
                role="region"
                id="pp-draw-results-extra-win-content"
                aria-labelledby="pp-draw-results-extra-win"
            >
                <ul class="pp-draw-numbers__num-row pp-draw-numbers__num-row--extra">
                    <li class="pp-draw-numbers__number" v-for="(num, index) in results.extraWins" :key="index">
                        {{ num }}
                    </li>
                </ul>
            </div>
        </PpDrawNumbersAccordion>
        <DsButton
            :is-external="true"
            :href="downloadLink"
            type="text"
            icon-right="arrow-right"
            v-if="results && !LotteryUtils.isQuickLottery(lottery)"
            size="sm"
            class="pp-draw-results-button__collapsible-btn--inset pp-draw-results-button"
        >
            Stáhnout výherní listinu
        </DsButton>

        <DsButton
            :href="LotteryUtils.resultsPageUrl(lottery)"
            type="text"
            icon-right="arrow-right"
            v-if="LotteryUtils.isQuickLottery(lottery)"
            class="pp-draw-results-button__collapsible-btn--inset pp-draw-results-button"
            size="sm"
        >
            Zobrazit starší výsledky
        </DsButton>

        <DsButton
            v-if="videoListId"
            :href="videoLink"
            type="text"
            icon-right="arrow-right"
            class="pp-draw-results-button__collapsible-btn--inset btn-video pp-draw-results-button"
            size="sm"
        >
            Video ze slosování
        </DsButton>
    </div>
</template>

<script lang="ts" setup>
import { type Component, computed, onMounted, useTemplateRef } from "vue";
import DsButton from "@sazka/design-system/src/button/ds-button.vue";
import { Lottery } from "@sazka/lotteries/js/models/lottery";
import PpEurojackpotWinnings from "@/lotteries/product-page/components/results/pp-eurojackpot-winnings.vue";
import PpSportkaWinnings from "@/lotteries/product-page/components/results/pp-sportka-winnings.vue";
import PpKenoWinnings from "@/lotteries/product-page/components/results/pp-keno-winnings.vue";
import PpStastnych10Winnings from "@/lotteries/product-page/components/results/pp-s10-winnings.vue";
import { useLotteryResults } from "@/lotteries/services/draw-info/pp-results";
import { LotteryUtils } from "@sazka/lotteries/js/utils/lottery-utils";
import PpDrawNumbersAccordion from "./pp-draw-numbers-accordion.vue";
import { useAccordion } from "@sazka/utils/composables/use-accordion";
import PpKasickaWinnings from "./pp-kasicka-winnings.vue";
import PpMiniRentaWinnings from "./pp-mini-renta-winnings.vue";
import PpEuromilionyWinnings from "./pp-euromiliony-winnings.vue";
import PpExtraRentaWinnings from "./pp-extra-renta-winnings.vue";
import { UrlUtils } from "@sazka/utils/url";
import PpKamenyWinnings from "./pp-kameny-winnings.vue";
import PpRychla6Winnings from "./pp-rychla-6-winnings.vue";
import PpStastneDatumWinnings from "./pp-stastne-datum-winnings.vue";

const { results } = useLotteryResults();
const wrapper = useTemplateRef("draw-results-wrapper");
const { init } = useAccordion({
    headerClassName: "pp-draw-results-button__collapsible-btn",
    bodyClassName: "pp-draw-results-button__collapsible-content",
});

const props = defineProps<{
    lottery: Lottery;
}>();

const resultsButton = useTemplateRef("resultsButton");

const configureStateFromUrl = () => {
    if (!resultsButton.value) return;

    const url = window.location.search;
    const urlParams = new URLSearchParams(url);

    const resultsParam = urlParams.get("results");

    if (resultsParam === "true") {
        resultsButton.value.$el.classList.add("is-open");
        resultsButton.value.$el.scrollIntoView({ behavior: "smooth" });
    }

    UrlUtils.clearUrlParam("results");
};

onMounted(() => {
    if (!wrapper.value) {
        return;
    }

    configureStateFromUrl();
    const items = wrapper.value.querySelectorAll("[data-accordion]");

    init(items);
});

const videoLists = {
    [Lottery.sportka]: "PL20598F1F704CC8AA",
    [Lottery.eurojackpot]: "PLYgWSC09bLw4jb1l9K_jcb6C4YLV-9rd8",
    [Lottery.euromiliony]: "PL40459FB6DC557B8F",
} as const;

type VideoList = (typeof videoLists)[keyof typeof videoLists];

const winnigTableTitle = computed(() => {
    switch (props.lottery) {
        case Lottery.keno:
        case Lottery.stastnych10:
        case Lottery.kasicka:
        case Lottery.kameny:
        case Lottery.rychla6:
        case Lottery.stastneDatum:
            return "Kolik můžu vyhrát?";
        default:
            return "Tabulka výher";
    }
});

const videoListId: VideoList = videoLists[props.lottery];

const getDrawComponent = (lottery: Lottery): Component => {
    switch (lottery) {
        case Lottery.eurojackpot:
            return PpEurojackpotWinnings;
        case Lottery.sportka:
            return PpSportkaWinnings;
        case Lottery.keno:
            return PpKenoWinnings;
        case Lottery.stastnych10:
            return PpStastnych10Winnings;
        case Lottery.kasicka:
            return PpKasickaWinnings;
        case Lottery.miniRenta:
            return PpMiniRentaWinnings;
        case Lottery.euromiliony:
            return PpEuromilionyWinnings;
        case Lottery.extraRenta:
            return PpExtraRentaWinnings;
        case Lottery.kameny:
            return PpKamenyWinnings;
        case Lottery.rychla6:
            return PpRychla6Winnings;
        case Lottery.stastneDatum:
            return PpStastneDatumWinnings;
    }
};
const tableComponent = getDrawComponent(props.lottery);

const downloadLink = computed(() => {
    return `/system/vyherka?year=${results.value?.year}&week=${results.value?.week}&game=${props.lottery}`;
});

const videoLink = computed(() => {
    const url = new URL("https://www.youtube-nocookie.com/embed/");

    url.searchParams.set("list", videoListId);
    url.searchParams.set("autoplay", "true");
    url.searchParams.set("wmode", "transparent");

    return url.toString();
});
</script>
